/**
 * batch-66 voertuigcontrole (FO-489 / spec 189) — gedeelde types voor de
 * zichtbare gezondheid van de drie voertuigcontroles: de RDW-sync, de
 * kentekenherkenning op inkoopfacturen en de loonstrook-voertuigcontrole.
 *
 * Alles hier is alleen-lezen weergave (integratiekaart + het herkenningspaneel
 * op de inkoopfactuur). Er staan uitsluitend aantallen, tijden en kentekens in,
 * nooit bedragen of persoonsgegevens.
 */

// ─── RDW-sync ────────────────────────────────────────────────────────────────

/** Waarom een RDW-run eerder stopte dan de vloot lang is. */
export const RDW_SYNC_AFGEBROKEN = ['TIJDSBUDGET', 'RDW_ONBEREIKBAAR'] as const
export type RdwSyncAfgebroken = (typeof RDW_SYNC_AFGEBROKEN)[number]

export const RDW_SYNC_AFGEBROKEN_LABELS: Record<RdwSyncAfgebroken, string> = {
  TIJDSBUDGET: 'het tijdsbudget van de run was op',
  RDW_ONBEREIKBAAR: 'RDW was niet bereikbaar',
}

/** Signalen op de integratiekaart (en de notificaties erachter). */
export const VOERTUIGCONTROLE_SIGNAAL_CODE = [
  /** De laatste run gaf meer dan 20 procent fouten. */
  'FOUTPERCENTAGE',
  /** De laatste run stopte voortijdig (tijdsbudget of RDW onbereikbaar). */
  'RUN_AFGEBROKEN',
  /** Langer dan 36 uur geen geslaagde run. */
  'GEEN_GESLAAGDE_RUN',
  /** De cron-rij ontbrak en is opnieuw aangemaakt met de standaard. */
  'CRON_RIJ_HERSTELD',
  /** De planning bestaat, maar in dit proces staat geen taak ingepland. */
  'CRON_NIET_INGEPLAND',
  /** Een run staat al langer dan een uur op "bezig" (proces gestopt tijdens de run). */
  'RUN_VASTGELOPEN',
] as const
export type VoertuigcontroleSignaalCode = (typeof VOERTUIGCONTROLE_SIGNAAL_CODE)[number]

export interface VoertuigcontroleSignaalDTO {
  code: VoertuigcontroleSignaalCode
  ernst: 'waarschuwing' | 'fout'
  /** Leesbare melding voor de beheerder (geen em-dash, merknaam STREEP). */
  melding: string
}

/** Meer dan dit aandeel fouten in een run geeft een signaal. */
export const RDW_FOUT_DREMPEL = 0.2
/**
 * Na zoveel uur zonder geslaagde run volgt een signaal, bij een planning van een
 * of twee keer per dag. Bij een ruimere planning geldt `rdwStilteGrensUren`.
 */
export const RDW_STILTE_UREN = 36

/** Het interval van elke planning-frequentie in uren (maand = 31 dagen, kwartaal = 92). */
const RDW_INTERVAL_UREN = {
  DAILY: 24,
  BIDAILY: 12,
  WEEKLY: 7 * 24,
  MONTHLY: 31 * 24,
  QUARTERLY: 92 * 24,
} as const
/** Ruimte bovenop het interval voordat "geen geslaagde run" een signaal is. */
const RDW_STILTE_MARGE_UREN = 12

/**
 * De grens voor "te lang geen geslaagde run" bij deze planning: het interval van
 * de planning plus twaalf uur, met 36 uur als ondergrens. Een vaste grens van 36
 * uur zou bij een wekelijkse planning elke dag een vals alarm geven.
 */
export function rdwStilteGrensUren(
  frequency: keyof typeof RDW_INTERVAL_UREN | null | undefined,
): number {
  if (!frequency) return RDW_STILTE_UREN
  return Math.max(RDW_STILTE_UREN, RDW_INTERVAL_UREN[frequency] + RDW_STILTE_MARGE_UREN)
}

/** Aantallen over `Voertuig.rdwLaatsteSyncStatus` van het wagenpark (de sync-selectie). */
export interface RdwVoertuigAggregaatDTO {
  totaal: number
  ok: number
  fout: number
  nietGevonden: number
  /** Nog nooit gesynchroniseerd (geen status). */
  nooitGesynchroniseerd: number
  /**
   * Langer dan `stilteGrensUren` geleden of nooit gesynchroniseerd: de run sloeg
   * ze over.
   */
  achterstand: number
}

export interface RdwRunSamenvattingDTO {
  id: string
  gestartOp: string
  klaarOp: string | null
  duurMs: number | null
  status: 'RUNNING' | 'SUCCESS' | 'FAILED'
  trigger: 'CRON' | 'MANUAL'
  totaal: number | null
  ok: number | null
  nietGevonden: number | null
  fouten: number | null
  /** Voertuigen die deze run niet aan de beurt kwamen (afgebroken run). */
  overgeslagen: number | null
  afgebroken: RdwSyncAfgebroken | null
  /** Foutmelding van de run als geheel (status FAILED). */
  fout: string | null
}

export interface RdwPlanningDTO {
  frequency: 'DAILY' | 'BIDAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY'
  hour: number
  minute: number
  timezone: string
}

export interface RdwGezondheidDTO {
  voertuigen: RdwVoertuigAggregaatDTO
  /** De foutmelding die nu op de meeste voertuigen staat, met het aantal. */
  meestVoorkomendeFout: { melding: string; aantal: number } | null
  laatsteRun: RdwRunSamenvattingDTO | null
  laatsteGeslaagdeRunOp: string | null
  urenSindsGeslaagdeRun: number | null
  /** De grens (in uren) waarboven "geen geslaagde run" een signaal is, bij deze planning. */
  stilteGrensUren: number
  volgendeRunOp: string | null
  planning: RdwPlanningDTO | null
  signalen: VoertuigcontroleSignaalDTO[]
}

// ─── Tellers ─────────────────────────────────────────────────────────────────

/** Het venster van de inkoop-teller in dagen. */
export const INKOOP_KENTEKEN_TELLER_DAGEN = 30

/**
 * "Inkoopfacturen met kenteken zonder voertuigvoorstel (30 d)". `zichtbaar:
 * false` = de kijker mist INKOOP-leesrecht; dan gaan er ook geen aantallen mee.
 */
export type InkoopKentekenTellerDTO =
  | {
      zichtbaar: true
      periodeDagen: number
      /** Facturen waarbij de extractie een kenteken las (op een regel of in de kop). */
      facturenMetKenteken: number
      /**
       * Daarvan: geen enkele regel heeft een voertuig (voorstel of definitief), de
       * factuur is nog te behandelen (ONTVANGEN of IN_BEHANDELING) en minstens één
       * regel met een bedrag heeft geen toewijzing die iemand zelf koos. Een
       * automatische besteltraject-boeking is geen keuze; een goedgekeurde factuur
       * of een regel die iemand zelf toewees wel.
       */
      zonderVoertuigvoorstel: number
      /** De nieuwste facturen zonder voorstel, om door te klikken (hooguit tien). */
      voorbeelden: { id: string; factuurNr: string }[]
    }
  | { zichtbaar: false }

/**
 * "Loonstrookregels met controle GEEN (laatste run)". `zichtbaar: false` = de
 * kijker mag geen loonstroken inzien; dan gaan er ook geen aantallen mee.
 */
export type LoonstrookGeenTellerDTO =
  | {
      zichtbaar: true
      /** De laatst gedraaide controle; null = er is nog geen run geweest. */
      rapport: { id: string; periodeLabel: string; gedraaidOp: string } | null
      /** Stroken waar de cascade geen voertuig vond. */
      strokenGeen: number
      /** Stroken waar meerdere voertuigen passen (handmatig koppelen). */
      strokenNietEenduidig: number
    }
  | { zichtbaar: false }

export interface VoertuigcontroleGezondheidDTO {
  rdw: RdwGezondheidDTO
  inkoop: InkoopKentekenTellerDTO
  loonstrook: LoonstrookGeenTellerDTO
  gemetenOp: string
}

// ─── Kentekenherkenning op een inkoopfactuur ─────────────────────────────────

/** Waar een herkend kenteken vandaan komt. */
export const KENTEKEN_HERKENNING_BRON = [
  /** Het kenteken dat de extractie bij deze regel las. */
  'EXTRACTIE_REGEL',
  /** Een kenteken in de omschrijving van de regel (streepjes-notatie). */
  'TEKST_REGEL',
  /** Het enige kenteken van de hele factuur (kop, omschrijving of een andere regel). */
  'FACTUUR',
] as const
export type KentekenHerkenningBron = (typeof KENTEKEN_HERKENNING_BRON)[number]

// "uit de omschrijving": bewust zonder het woord kenteken. Een onbekend token uit
// een omschrijving kan ook een artikelnummer zijn; de melding eronder zegt dat.
export const KENTEKEN_HERKENNING_BRON_LABELS: Record<KentekenHerkenningBron, string> = {
  EXTRACTIE_REGEL: 'kenteken op de regel',
  TEKST_REGEL: 'uit de omschrijving van de regel',
  FACTUUR: 'enige kenteken op de factuur',
}

/** Waarom er bij een leesbaar kenteken géén voertuigvoorstel is. */
export const KENTEKEN_GEEN_VOORSTEL_REDEN = [
  /** Geen voertuig met dit kenteken in het wagenpark. */
  'ONBEKEND_KENTEKEN',
  /** Meerdere voertuigen met dit kenteken en geen ervan eenduidig actief op de factuurdatum. */
  'MEERDERE_VOERTUIGEN',
  /** Meerdere kentekens bij deze regel of op de factuur: niet te zeggen welk voertuig. */
  'MEERDERE_KENTEKENS',
  /** De regel heeft geen bedrag; er valt niets toe te wijzen. */
  'GEEN_BEDRAG',
  /** De regel is al door iemand toegewezen; de engine legt daar niets naast. */
  'AL_TOEGEWEZEN',
  /**
   * De regel is automatisch via het besteltraject geboekt en er staat (nu) geen
   * voertuigvoorstel naast. "Suggesties opnieuw bepalen" zet het er alsnog naast.
   */
  'BESTELTRAJECT_GEBOEKT',
  /** De regel staat op een ANDER voertuig dan het kenteken dat erbij herkend is. */
  'ANDER_VOERTUIG',
  /**
   * Het kenteken is alleen op factuurniveau bekend en een voorstel op de regel
   * zelf (herkende naam, of de herhaling van een eerdere keuze) ging voor.
   */
  'ANDER_VOORSTEL',
  /** Het voertuig is bekend, maar de suggesties zijn sindsdien niet (opnieuw) bepaald. */
  'NOG_NIET_BEPAALD',
] as const
export type KentekenGeenVoorstelReden = (typeof KENTEKEN_GEEN_VOORSTEL_REDEN)[number]

/**
 * De redenen die om een handeling van de reviewer vragen. De overige zijn ter
 * informatie: daar is al een keuze gemaakt (al toegewezen, via het besteltraject
 * geboekt, een ander voorstel ging voor) of valt niets te kiezen (geen bedrag).
 */
export const KENTEKEN_REDEN_AANDACHT: readonly KentekenGeenVoorstelReden[] = [
  'ONBEKEND_KENTEKEN',
  'MEERDERE_VOERTUIGEN',
  'MEERDERE_KENTEKENS',
  'NOG_NIET_BEPAALD',
  'ANDER_VOERTUIG',
]

/** Vraagt deze reden om een handeling van de reviewer? */
export function isKentekenRedenAandacht(reden: KentekenGeenVoorstelReden | null): boolean {
  return reden !== null && KENTEKEN_REDEN_AANDACHT.includes(reden)
}

/** Kan "Suggesties opnieuw bepalen" bij deze reden een voertuigvoorstel opleveren? */
export function isKentekenRedenOpnieuwBepaalbaar(reden: KentekenGeenVoorstelReden | null): boolean {
  return reden === 'NOG_NIET_BEPAALD' || reden === 'BESTELTRAJECT_GEBOEKT'
}

export interface InkoopKentekenRegelHerkenningDTO {
  regelId: string
  position: number
  omschrijving: string
  /**
   * Het herkende kenteken in leesbare vorm (V-65-PXH); bij meerdere de lijst. Een
   * onbekend token uit de omschrijving staat er letterlijk zoals het in de tekst
   * stond (het kan ook een artikelnummer zijn).
   */
  kentekens: string[]
  bron: KentekenHerkenningBron
  /** Het voertuig dat bij het kenteken hoort, als dat eenduidig is. */
  voertuig: { id: string; kenteken: string; actief: boolean } | null
  /** Heeft de regel nu een voertuig-toewijzing (voorstel of definitief)? */
  heeftVoertuigToewijzing: boolean
  /**
   * Gevuld wanneer er géén voertuigvoorstel is, of wanneer de regel op een ander
   * voertuig staat dan het herkende kenteken (`ANDER_VOERTUIG`).
   */
  reden: KentekenGeenVoorstelReden | null
  /** Leesbare uitleg bij `reden` (of null als alles klopt). */
  melding: string | null
}

export interface InkoopKentekenHerkenningDTO {
  factuurId: string
  /**
   * De kentekens die de factuur noemt (leesbare vorm). Een onbekend token dat
   * alleen in de omschrijving van één regel staat hoort hier niet bij; dat staat
   * bij die regel.
   */
  factuurKentekens: string[]
  regels: InkoopKentekenRegelHerkenningDTO[]
  /** Aantal regels met een leesbaar kenteken zonder voertuig-toewijzing. */
  zonderVoorstel: number
  /**
   * Kentekens die op de factuur gelezen zijn maar bij geen enkele regel horen
   * (de regels zijn sinds de import bewerkt en niet meer te herleiden, en er
   * staan meerdere kentekens op de factuur). Leesbare vorm.
   */
  nietGekoppeldeKentekens: string[]
}
